import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import { AxiosInstance } from 'axios';
import { RootState } from '../..';
import {
  defaultStyleGuide,
  defaultColors,
  defaultBorder,
  defaultButton,
  defaultBackground
} from '../../../styles/style-guide';
import { backgroundTypes, borderTypes, buttonTypes, colorTypes } from './MapColors';
import { ColorType, StyleGuideType } from './Types';

const initialState: StyleGuideType = {
  ...defaultStyleGuide,
  data: {
    colors: {},
    border: {},
    button: {},
    background: {},
    logo: ''
  }
};

export const fetchStyleGuide = createAsyncThunk<
  StyleGuideType,
  void,
  { state: RootState; extra: { api: AxiosInstance } }
>('style/fetchStyleGuide', async (_, { extra }) => {
  const { api } = extra;
  const { data } = await api.get('/instances');

  const colors = data.data.colors.reduce(
    (acc: typeof defaultColors, color: ColorType) => {
      if (color.type === 'text') {
        const colorTypeKey = colorTypes[color.order];
        if (colorTypeKey) {
          acc[colorTypeKey] = color.color;
        }
      }
      return acc;
    },
    { ...defaultColors }
  );

  const border = data.data.colors.reduce(
    (acc: typeof defaultBorder, color: ColorType) => {
      if (color.type === 'border') {
        const borderTypeKey = borderTypes[color.order];
        if (borderTypeKey) {
          acc[borderTypeKey] = color.color;
        }
      }
      return acc;
    },
    { ...defaultBorder }
  );

  const button = data.data.colors.reduce(
    (acc: typeof defaultButton, color: ColorType) => {
      if (color.type === 'button') {
        const buttonTypeKey = buttonTypes[color.order];
        if (buttonTypeKey) {
          acc[buttonTypeKey] = color.color;
        }
      }
      return acc;
    },
    { ...defaultButton }
  );

  const background = data.data.colors.reduce(
    (acc: typeof defaultBackground, color: ColorType) => {
      if (color.type === 'background') {
        const backgroundTypeKey = backgroundTypes[color.order];
        if (backgroundTypeKey) {
          acc[backgroundTypeKey] = color.color;
        }
      }
      return acc;
    },
    { ...defaultBackground }
  );

  return {
    ...defaultStyleGuide,
    data: {
      colors,
      border,
      button,
      background,
      logo: data.data.logo
    }
  };
});

export const styleSlice = createSlice({
  name: 'style',
  initialState,
  reducers: {
    setStyleGuide(state, action: PayloadAction<StyleGuideType>) {
      return action.payload;
    },
    updateStyleColors(state, action: PayloadAction<Partial<StyleGuideType['data']>>) {
      if (action.payload.colors) {
        state.data.colors = {
          ...state.data.colors,
          ...action.payload.colors
        };
      }
      if (action.payload.button) {
        state.data.button = {
          ...state.data.button,
          ...action.payload.button
        };
      }
      if (action.payload.border) {
        state.data.border = {
          ...state.data.border,
          ...action.payload.border
        };
      }
      if (action.payload.background) {
        state.data.background = {
          ...state.data.background,
          ...action.payload.background
        };
      }
      if (action.payload.logo) {
        state.data.logo = action.payload.logo;
      }
    }
  },
  extraReducers: (builder) => {
    builder.addCase(fetchStyleGuide.fulfilled, (state, action: PayloadAction<StyleGuideType>) => {
      return action.payload;
    });
  }
});

export const { setStyleGuide, updateStyleColors } = styleSlice.actions;
export const styleGuideSelector = (state: RootState) => state.styleGuide;
export default styleSlice.reducer;
